import React, { Component, ContextType } from 'react';
import { SearchContext } from '../../context/SearchContext';
import { facets } from '../search/facets/facetLogic';
import log from 'loglevel';
import FacetFieldPiecharts from "./facets/FacetFieldPiecharts";
import './AnalyticsResult.scss';
import { Field } from '../../types/Field';

interface FacetValue {
  [key: string]: { count: number };
}

interface FacetSelections {
  [key: string]: string[];
}

interface AnalyticsResultState {
  isPanelOpen: boolean;
  facetExpanded: string;
}

class AnalyticsResult extends Component<{}, AnalyticsResultState> {
  static contextType = SearchContext;
  context!: ContextType<typeof SearchContext>;

  state: AnalyticsResultState = {
    isPanelOpen: false,
    facetExpanded: ''
  };

  buildPieChartData = (pieFacetData: FacetValue) => {
    let resultDataArr: { name: string; value: number }[] = [];
    let index = 1;
    let othersCount = 0;
    const facetDataThreshold = 10;

    pieFacetData = Object.fromEntries(
      Object.entries(pieFacetData).filter(([_, value]) => value.count !== 0)
    );

    for (const valueKey in pieFacetData) {
      const value = pieFacetData[valueKey];
      const countDisplay = value.count ? value.count : 0;
      let displayValue = String(valueKey);
      let pieDataJsn = { name: '', value: 0 };

      if (index > facetDataThreshold) {
        break;
      } else {
        othersCount = othersCount + Number(countDisplay);
        pieDataJsn.name = displayValue;
        pieDataJsn.value = Number(countDisplay);
        if (pieDataJsn.value !== 0)
          resultDataArr.push(pieDataJsn);
      }
      index = index + 1;
    }

    let pieDataJsn = { name: 'Others', value: 0 };
    let totalCount = Object.values(pieFacetData).reduce((sum, entry) => sum + entry.count, 0);
    pieDataJsn.value = Number(totalCount) - Number(othersCount);
    if (pieDataJsn.value > 0)
      resultDataArr.push(pieDataJsn);

    return resultDataArr;
  };

  getFecetFilterData = (facetSelections: FacetSelections) => {
    let filterDataArr: { key: string; values: string[]; name: string }[] = [];
    let facetSelectedKeys = Object.keys(facetSelections);
    facetSelectedKeys.forEach((element) => {
      let filterDataJson = {
        key: '',
        values: [] as string[],
        name: '',
      };
      if (element !== 'TimeStart' && element !== 'Duration') {
        const field = this.context.filterableUIFields?.find((f: Field) => f.name === element);
        if (!field) return;
        filterDataJson.key = field.systemSettings.displayName;
        filterDataJson.values = facetSelections[element];
        filterDataJson.name = field.name;
        filterDataArr.push(filterDataJson);
      }
    });
    return filterDataArr;
  };

  facetChanged = (e: { name: string }, fieldName: string) => {
    const field = this.context.filterableUIFields?.find((f: Field) => f.name === fieldName);
    if (!field) return;
    const facetLogic = facets.getFacetLogicByType(field.systemSettings.type);
    if (e.name === "Others")
      return;
    if (!facetLogic) {
      log.error(`[facetChanged] Unexpected data type found: ${field.systemSettings.type}`);
      return;
    }
    let selectedFecets = this.context.facetSelections[fieldName];
    let isFecetFiltered = false;
    selectedFecets?.forEach((element: string) => {
      if (element === e.name)
        isFecetFiltered = true;
    });
    if (!isFecetFiltered)
      this.context.updateFacetSelection(fieldName, e.name, facetLogic.createUpdatedSelection);
  }

  render() {
    const fields: Field[] = this.context.filterableUIFields || [];
    let facetArray: { key: string; displayName: string }[] = [];
    if (fields === undefined)
      return null;
    fields.forEach(field => {
      let facetJson = { key: '', displayName: '' };
      let facetValue = this.context.facetValues[field.name];
      if (!field.userSettings.showInFacetArea || facetValue === undefined) {
        return;
      }
      if (field.name === "TimeStart" || field.name === "Duration") {
        return;
      }
      facetJson.key = field.name;
      facetJson.displayName = field.systemSettings.displayName;
      facetArray.push(facetJson);
    });
    const facetSelection = this.context.facetSelections[this.state.facetExpanded];
    let selectedFilterData = this.getFecetFilterData(this.context.facetSelections);
    return (
      <div className="analytics-div">
        {
          facetArray.map(element => (
            <PieChartFacetField key={element.key}
              selectedFilterData={selectedFilterData}
              fieldName={element.key}
              name={element.displayName}
              onChangeFacetPie={this.facetChanged}
              facetSelection={facetSelection || []}
              pieData={this.buildPieChartData(this.context.facetValues[element.key])}
            />
          ))
        }
      </div>
    );
  }
}

export default AnalyticsResult;

interface PieChartFacetFieldProps {
  selectedFilterData: { key: string; values: string[]; name: string }[];
  fieldName: string;
  name: string;
  onChangeFacetPie: (e: { name: string }, fieldName: string) => void;
  facetSelection: string[];
  pieData: { name: string; value: number }[];
}

const PieChartFacetField: React.FC<PieChartFacetFieldProps> = ({ selectedFilterData, fieldName, name, onChangeFacetPie, facetSelection, pieData }) => {
  return (
    <FacetFieldPiecharts selectedFilterData={selectedFilterData} onChangeFacetPie={onChangeFacetPie} fieldName={fieldName} name={name} facetSelection={facetSelection} pieDataArr={pieData} />
  );
}
